import { createContext, useCallback, useContext, useRef, useState } from "react";
import { LocalProgress, PuzzleResult } from "../types";
import { PROGRESS_KEY, GAME_STATE_KEY, puzzleKey, computeStars, getUtcToday, addDays } from "../lib/constants";
import { syncProgress as apiSyncProgress } from "../lib/api";

/** Max days allowed between plays without breaking the streak */
const MAX_PAUSE_DAYS = 2;

export type DayStatus = "played" | "paused" | "empty" | "future";

/**
 * Count current streak (with up to MAX_PAUSE_DAYS pause tolerance)
 * and longest streak from a set of unique date strings.
 * Also returns recent day history for the streak popup.
 */
function computeStreaks(dates: string[]): {
  current: number;
  longest: number;
  pausedDays: number;
  recentDays: { date: string; status: DayStatus }[];
} {
  const today = getUtcToday();
  const dateSet = new Set(dates);

  if (dates.length === 0) return { current: 0, longest: 0, pausedDays: 0, recentDays: buildRecentDays(today, dateSet) };

  const sorted = [...dates].sort();

  // Compute longest streak (with pause tolerance — only count played days)
  let longest = 1;
  let run = 1;
  for (let i = 1; i < sorted.length; i++) {
    const gap = daysBetween(sorted[i - 1], sorted[i]);
    if (gap <= MAX_PAUSE_DAYS + 1) {
      run++;
    } else {
      if (run > longest) longest = run;
      run = 1;
    }
  }
  if (run > longest) longest = run;

  // Current streak: walk backwards from most recent play
  // Must be reachable from today (within MAX_PAUSE_DAYS)
  const last = sorted[sorted.length - 1];
  const daysSinceLast = daysBetween(last, today);
  let current = 0;
  let pausedDays = 0;

  // Allow daysSinceLast up to MAX_PAUSE_DAYS + 1 (e.g., 3 days ago = 2 skipped days)
  if (daysSinceLast <= MAX_PAUSE_DAYS + 1) {
    current = 1;
    // pausedDays = skipped days (yesterday = 0, 2 days ago = 1, 3 days ago = 2)
    pausedDays = Math.max(0, daysSinceLast - 1);
    for (let i = sorted.length - 2; i >= 0; i--) {
      const gap = daysBetween(sorted[i], sorted[i + 1]);
      if (gap <= MAX_PAUSE_DAYS + 1) {
        current++;
      } else {
        break;
      }
    }
  }

  return { current, longest, pausedDays, recentDays: buildRecentDays(today, dateSet) };
}

/** Number of days between two YYYY-MM-DD strings */
function daysBetween(a: string, b: string): number {
  const da = new Date(a + "T00:00:00Z").getTime();
  const db = new Date(b + "T00:00:00Z").getTime();
  return Math.round(Math.abs(db - da) / (24 * 60 * 60 * 1000));
}

/** Build recent 7-day history for streak popup */
function buildRecentDays(today: string, playedDates: Set<string>): { date: string; status: DayStatus }[] {
  const days: { date: string; status: DayStatus }[] = [];
  for (let i = 6; i >= 0; i--) {
    const date = addDays(today, -i);
    if (date > today) {
      days.push({ date, status: "future" });
    } else if (playedDates.has(date)) {
      days.push({ date, status: "played" });
    } else {
      days.push({ date, status: "empty" });
    }
  }

  // Mark paused days: empty days that fall within an active streak.
  // Find the earliest played date in the streak (walking back with pause tolerance)
  // and mark all empty days between that and today as paused.
  const playedIndices = days.map((d, i) => d.status === "played" ? i : -1).filter((i) => i >= 0);
  if (playedIndices.length > 0) {
    const lastPlayed = playedIndices[playedIndices.length - 1];
    // Walk backwards to find streak start
    let streakStart = lastPlayed;
    for (let i = playedIndices.length - 2; i >= 0; i--) {
      const gap = playedIndices[i + 1] - playedIndices[i] - 1; // empty days between
      if (gap <= MAX_PAUSE_DAYS) {
        streakStart = playedIndices[i];
      } else {
        break;
      }
    }
    // If the streak extends before the 7-day window, empty days at the start
    // of the window are also paused (not truly empty).
    if (streakStart > 0) {
      const windowStartDate = days[0].date;
      for (let d = 1; d <= MAX_PAUSE_DAYS + 1; d++) {
        const checkDate = addDays(windowStartDate, -d);
        if (playedDates.has(checkDate)) {
          if (daysBetween(checkDate, days[streakStart].date) <= MAX_PAUSE_DAYS + 1) {
            for (let i = 0; i < streakStart; i++) {
              if (days[i].status === "empty" && days[i].date < today) {
                days[i] = { ...days[i], status: "paused" };
              }
            }
          }
          break;
        }
      }
    }
    // Mark empty days from streak start to today (end of array) as paused
    for (let i = streakStart; i < days.length; i++) {
      if (days[i].status === "empty" && days[i].date < today) {
        days[i] = { ...days[i], status: "paused" };
      }
    }
  }

  return days;
}

function loadProgress(): LocalProgress {
  try {
    const raw = localStorage.getItem(PROGRESS_KEY);
    if (!raw) return {};
    const data = JSON.parse(raw);
    // Migrate: rename solvedAtDate → completedAtDate, solvedAtMs → completedAtMs,
    // and backfill completedAtDate from game state for entries that have no date at all.
    let gameStates: Record<string, { completedAtDate?: string }> | null = null;
    let migrated = false;
    for (const [key, entry] of Object.entries(data) as [string, Record<string, unknown>][]) {
      if ("solvedAtDate" in entry) {
        entry.completedAtDate = entry.solvedAtDate;
        delete entry.solvedAtDate;
        migrated = true;
      }
      if ("solvedAtMs" in entry) {
        entry.completedAtMs = entry.solvedAtMs;
        delete entry.solvedAtMs;
        migrated = true;
      }
      if (!entry.completedAtDate) {
        // Backfill from game state
        if (!gameStates) {
          try { gameStates = JSON.parse(localStorage.getItem(GAME_STATE_KEY) || "{}"); } catch { gameStates = {}; }
        }
        const gs = gameStates![key];
        if (gs?.completedAtDate) {
          entry.completedAtDate = gs.completedAtDate;
          migrated = true;
        }
      }
    }
    if (migrated) {
      localStorage.setItem(PROGRESS_KEY, JSON.stringify(data));
    }
    return data;
  } catch {
    return {};
  }
}

function saveProgress(progress: LocalProgress): void {
  try {
    localStorage.setItem(PROGRESS_KEY, JSON.stringify(progress));
  } catch {
    // localStorage might be full or unavailable
  }
}

export interface AggregateStats {
  totalGames: number;
  wins: number;
  firstTryWins: number;
  retryWins: number;
  losses: number;
  incomplete: number;
  winPercent: number;
  firstTryWinPercent: number;
  lossPercent: number;
  incompletePercent: number;
  avgTimeMs: number;
  avgFocusedMs: number;
  avgStars: number;
  currentWinStreak: number;
  longestWinStreak: number;
}

/**
 * Compute aggregate stats from a list of keyed results.
 * Results must be pre-sorted in ascending chronological order for correct streak computation.
 * Used by both the unfiltered getStats path and the difficulty-filtered Stats component.
 */
export function computeAggregateStats(results: { key: string; result: PuzzleResult }[]): AggregateStats {
  const entries = results.map((r) => r.result);
  const totalGames = entries.length;
  const firstTryWinEntries = entries.filter((e) => e.solved && !e.overLimit && e.firstTry);
  const wins = firstTryWinEntries.length;
  const retryWins = entries.filter((e) => e.solved && !e.overLimit && !e.firstTry).length;
  const losses = entries.filter((e) => e.solved && e.overLimit).length;
  const incomplete = entries.filter((e) => !e.solved).length;
  const completedTimes = firstTryWinEntries.map((e) => e.elapsedMs);
  const avgTimeMs = completedTimes.length > 0
    ? Math.round(completedTimes.reduce((a, b) => a + b, 0) / completedTimes.length) : 0;
  const focusedTimes = firstTryWinEntries.filter((e) => e.focusedMs != null).map((e) => e.focusedMs!);
  const avgFocusedMs = focusedTimes.length > 0
    ? Math.round(focusedTimes.reduce((a, b) => a + b, 0) / focusedTimes.length) : 0;
  const winStars = firstTryWinEntries.map((e) => e.stars);
  const avgStars = winStars.length > 0
    ? winStars.reduce((a, b) => a + b, 0) / winStars.length : 0;

  // Win streak: walk results in the order provided (caller sorts chronologically).
  // Retried puzzles (attempts > 1) break the streak — the first attempt failed,
  // so replaying a solved puzzle cannot extend a streak.
  let currentWinStreak = 0;
  let longestWinStreak = 0;
  for (const r of results) {
    if (!r.result.solved && !r.result.overLimit) continue; // incomplete, skip
    if (r.result.overLimit || r.result.attempts > 1) {
      currentWinStreak = 0;
    } else {
      currentWinStreak++;
      if (currentWinStreak > longestWinStreak) longestWinStreak = currentWinStreak;
    }
  }

  return {
    totalGames, wins, firstTryWins: wins, retryWins,
    losses, incomplete,
    winPercent: totalGames > 0 ? Math.round((wins / totalGames) * 100) : 0,
    firstTryWinPercent: totalGames > 0 ? Math.round((wins / totalGames) * 100) : 0,
    lossPercent: totalGames > 0 ? Math.round((losses / totalGames) * 100) : 0,
    incompletePercent: totalGames > 0 ? Math.round((incomplete / totalGames) * 100) : 0,
    avgTimeMs, avgFocusedMs, avgStars, currentWinStreak, longestWinStreak,
  };
}

interface ProgressContextValue {
  progress: LocalProgress;
  markSolved: (date: string, size: string, swapsUsed: number, maxSwaps: number, elapsedMs: number, focusedMs?: number) => void;
  /** Returns a promise that resolves when the most recent sync completes. */
  waitForSync: () => Promise<void>;
  isSolved: (date: string, size?: string) => boolean;
  getResult: (date: string, size: string) => PuzzleResult | null;
  getStats: (sizeFilter?: string) => AggregateStats;
  getPlayStreak: (sizeFilter?: string) => { current: number; longest: number; pausedDays: number; recentDays: { date: string; status: DayStatus }[] };
  getAllResults: (sizeFilter?: string) => { key: string; result: PuzzleResult }[];
  invalidateStreaks: () => void;
  syncWithServer: () => Promise<void>;
}

export const ProgressContext = createContext<ProgressContextValue | null>(null);

/**
 * Hook that provides shared progress state via ProgressContext.
 * Must be used within a ProgressContext.Provider.
 */
export function useLocalProgress(): ProgressContextValue {
  const ctx = useContext(ProgressContext);
  if (!ctx) {
    throw new Error("useLocalProgress must be used within a ProgressContext.Provider");
  }
  return ctx;
}

/**
 * Creates the actual progress state. Used once in the provider.
 */
export function useLocalProgressState(): ProgressContextValue {
  const [progress, setProgress] = useState<LocalProgress>(loadProgress);
  const [streakVersion, setStreakVersion] = useState(0);
  const invalidateStreaks = useCallback(() => setStreakVersion((v) => v + 1), []);

  const markSolved = useCallback(
    (date: string, size: string, swapsUsed: number, maxSwaps: number, elapsedMs: number, focusedMs?: number) => {
      const swapsRemaining = maxSwaps - swapsUsed;
      const overLimit = swapsRemaining < 0;
      const stars = computeStars(swapsRemaining);
      const key = puzzleKey(size, date);

      setProgress((prev) => {
        const existing = prev[key];
        const attempts = (existing?.attempts ?? 0) + 1;
        const firstTry = attempts === 1;
        const newEntry = { solved: true, swapsUsed, maxSwaps, stars, elapsedMs, focusedMs, size, overLimit, attempts, firstTry, completedAtDate: getUtcToday(), completedAtMs: Date.now() };

        // Keep the better result: if already solved within limit, only overwrite with fewer swaps
        if (existing?.solved && !existing.overLimit && !overLimit && existing.swapsUsed <= swapsUsed) {
          // Existing is better or equal — just update attempts count
          const next = { ...prev, [key]: { ...existing, attempts } };
          saveProgress(next);
          return next;
        }

        const next = { ...prev, [key]: newEntry };
        saveProgress(next);
        return next;
      });
    },
    []
  );

  const isSolved = useCallback(
    (date: string, size?: string): boolean => {
      if (size) {
        return progress[puzzleKey(size, date)]?.solved ?? false;
      }
      return Object.keys(progress).some(
        (k) => k.endsWith(`:${date}`) && progress[k].solved
      );
    },
    [progress]
  );

  const getResult = useCallback(
    (date: string, size: string): PuzzleResult | null => {
      return progress[puzzleKey(size, date)] ?? null;
    },
    [progress]
  );

  const getStats = useCallback((sizeFilter?: string): AggregateStats => {
    // Include unsolved over-limit games from game state as streak-breakers
    let gameStates: Record<string, { swapsUsed: number; maxSwaps?: number; completedAtDate?: string }> = {};
    try { gameStates = JSON.parse(localStorage.getItem(GAME_STATE_KEY) || "{}"); } catch { /* ignore */ }

    // Build keyed results from progress (authoritative source after sync merge)
    const progressEntries = Object.entries(progress)
      .filter(([k]) => (!sizeFilter || k.startsWith(`${sizeFilter}:`)) && !k.endsWith(":random"))
      .map(([key, result]) => ({ key, result }));

    const progressKeys = new Set(Object.keys(progress));
    const extraEntries = Object.entries(gameStates)
      .filter(([k, gs]) => (!sizeFilter || k.startsWith(`${sizeFilter}:`)) && !k.endsWith(":random")
        && !progressKeys.has(k) && gs.maxSwaps != null && gs.swapsUsed >= gs.maxSwaps)
      .map(([key, gs]) => ({
        key,
        result: {
          solved: false, swapsUsed: gs.swapsUsed, maxSwaps: gs.maxSwaps!,
          stars: 0, elapsedMs: 0, size: key.split(":")[0],
          overLimit: true, attempts: 1, firstTry: false,
          completedAtDate: gs.completedAtDate,
        } as PuzzleResult,
      }));

    const all = [...progressEntries, ...extraEntries].sort((a, b) => {
      const aDate = a.result.completedAtDate ?? "";
      const bDate = b.result.completedAtDate ?? "";
      return aDate.localeCompare(bDate) || (a.result.completedAtMs ?? 0) - (b.result.completedAtMs ?? 0);
    });
    return computeAggregateStats(all);
  }, [progress]);

  const getPlayStreak = useCallback((sizeFilter?: string) => {
    // Include solved puzzles (only those with a recorded completedAtDate)
    const solvedDates = Object.entries(progress)
      .filter(([k, v]) => v.solved && v.completedAtDate && (!sizeFilter || k.startsWith(`${sizeFilter}:`)) && !k.endsWith(":random"))
      .map(([, v]) => v.completedAtDate!);

    // Also include completed games from game state (win or over-limit, with completedAtDate)
    let gameStates: Record<string, { swapsUsed: number; maxSwaps?: number; completedAtDate?: string }> = {};
    try { gameStates = JSON.parse(localStorage.getItem(GAME_STATE_KEY) || "{}"); } catch { /* ignore */ }
    const progressKeys = new Set(Object.keys(progress));
    const completedDates = Object.entries(gameStates)
      .filter(([k, gs]) => (!sizeFilter || k.startsWith(`${sizeFilter}:`)) && !k.endsWith(":random")
        && !progressKeys.has(k) && gs.completedAtDate)
      .map(([, gs]) => gs.completedAtDate!);

    const uniqueDates = [...new Set([...solvedDates, ...completedDates])];
    return computeStreaks(uniqueDates);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [progress, streakVersion]);

  const getAllResults = useCallback((sizeFilter?: string): { key: string; result: PuzzleResult }[] => {
    return Object.entries(progress)
      .filter(([k]) => !sizeFilter || k.startsWith(`${sizeFilter}:`))
      .map(([key, result]) => ({ key, result }))
      .sort((a, b) => b.key.localeCompare(a.key));
  }, [progress]);

  // Merge server progress into local. Earliest completedAtMs wins.
  const applyServerProgress = useCallback((serverProgress: Record<string, PuzzleResult>) => {
    setProgress((prev) => {
      let changed = false;
      const next = { ...prev };
      for (const [key, serverEntry] of Object.entries(serverProgress)) {
        const local = prev[key];
        if (!local) {
          next[key] = serverEntry;
          changed = true;
        } else if (!local.solved && serverEntry.solved) {
          next[key] = serverEntry;
          changed = true;
        } else if (local.solved && serverEntry.solved) {
          // Same merge logic as server: within-limit > over-limit > fewer swaps > earlier solve
          const localOver = local.overLimit ?? false;
          const serverOver = serverEntry.overLimit ?? false;
          let serverWins = false;
          if (localOver && !serverOver) {
            serverWins = true;
          } else if (!localOver && serverOver) {
            serverWins = false;
          } else if (!localOver && !serverOver) {
            if (serverEntry.swapsUsed < local.swapsUsed) {
              serverWins = true;
            } else if (serverEntry.swapsUsed === local.swapsUsed) {
              serverWins = (serverEntry.completedAtMs ?? Infinity) < (local.completedAtMs ?? Infinity);
            }
          } else {
            serverWins = (serverEntry.completedAtMs ?? Infinity) < (local.completedAtMs ?? Infinity);
          }
          if (serverWins) {
            next[key] = serverEntry;
            changed = true;
          }
        }
      }
      if (changed) {
        saveProgress(next);
        return next;
      }
      return prev;
    });
  }, []);

  // Full sync: push local progress to server, apply merged result
  const syncWithServer = useCallback(async () => {
    const merged = await apiSyncProgress(progress as Record<string, unknown>);
    if (merged) applyServerProgress(merged as Record<string, PuzzleResult>);
  }, [progress, applyServerProgress]);

  // Background sync: debounced push after markSolved
  const syncTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const originalMarkSolved = markSolved;
  const lastSyncRef = useRef<Promise<void> | null>(null);
  const prevResolveRef = useRef<(() => void) | null>(null);
  const syncingMarkSolved = useCallback(
    (date: string, size: string, swapsUsed: number, maxSwaps: number, elapsedMs: number, focusedMs?: number) => {
      originalMarkSolved(date, size, swapsUsed, maxSwaps, elapsedMs, focusedMs);
      // Debounce: reschedule so the final state is always synced
      if (syncTimer.current) clearTimeout(syncTimer.current);
      // Resolve any orphaned promise from a previous debounced sync
      if (prevResolveRef.current) prevResolveRef.current();
      lastSyncRef.current = new Promise<void>((resolve) => {
        prevResolveRef.current = resolve;
        syncTimer.current = setTimeout(async () => {
          syncTimer.current = null;
          const current = loadProgress();
          try {
            const merged = await apiSyncProgress(current as Record<string, unknown>);
            if (merged) applyServerProgress(merged as Record<string, PuzzleResult>);
          } catch { /* network error — will retry on next solve or focus */ }
          lastSyncRef.current = null;
          prevResolveRef.current = null;
          resolve();
        }, 500);
      });
    },
    [originalMarkSolved, applyServerProgress]
  );

  const waitForSync = useCallback(() => lastSyncRef.current ?? Promise.resolve(), []);

  return { progress, markSolved: syncingMarkSolved, waitForSync, isSolved, getResult, getStats, getPlayStreak, getAllResults, invalidateStreaks, syncWithServer };
}
